import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TenantDto } from '@nusign/picflow-backend-types';
import { Subject, firstValueFrom, of, takeUntil } from 'rxjs';
import { debounce } from '../../../lib/debounce';
import { getFormControl } from '../../../lib/get-form-control';
import { TenantService } from '../../../services/tenant.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TransitionPreventorDirective } from '../../../shared/directives/transition-preventor.directive';

@Component({
  selector: 'app-create-tenant-form',
  templateUrl: './create-tenant-form.component.html',
  styleUrls: ['./create-tenant-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    InputPostDirective,
    SvgComponent,
    LoadingIndicatorComponent,
    ButtonDirective,
    TransitionPreventorDirective,
  ],
})
export class CreateTenantFormComponent implements OnInit, OnDestroy {
  private tenantService = inject(TenantService);
  @Input() tenant: TenantDto;
  @Input() cta: string;
  @Input() tenantPathError: boolean;
  @Input() hideCta: boolean;
  @Input() noMaxWidth: boolean;
  @Input() initialValue: { name: string; path: string };
  @Output() onDone = new EventEmitter();
  @Output() hoverChange = new EventEmitter<boolean>();
  @Output() focusChange = new EventEmitter<boolean>();
  workspaceForm = new FormGroup({
    name: getFormControl('businessName'),
    path: getFormControl('path') as FormControl<string>,
  });
  validTenantPath = signal(false);
  checkingTenantPath = signal(false);
  manuallyChangedPath = signal(false);
  savingWorkspace = signal(false);
  destroyed$ = new Subject<void>();

  ngOnInit(): void {
    if (this.initialValue) {
      this.workspaceForm.setValue(this.initialValue);
      this.checkPath();
    }
    this.workspaceForm.controls.name.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.manuallyChangedPath()) return;
      this.autoSetPath();
    });

    this.workspaceForm.controls.path.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.checkingTenantPath.set(true);
      this.checkPathDebounced();
    });
  }

  autoSetPath = debounce(() => {
    const path = this.workspaceForm.value.name.toLowerCase().replace(/[\W_]+/g, '');
    this.workspaceForm.controls.path.setValue(path);
  }, 500);

  async checkPath() {
    if (!this.workspaceForm.valid) {
      this.checkingTenantPath.set(false);
      this.validTenantPath.set(false);
      return;
    }
    const slug = this.workspaceForm.value.path.toLowerCase();

    this.checkingTenantPath.set(true);
    this.validTenantPath.set(await firstValueFrom(this.getSlugValidity(slug)));
    this.checkingTenantPath.set(false);
  }

  checkPathDebounced = debounce(async () => {
    await this.checkPath();
  }, 500);

  private getSlugValidity(path: string) {
    if (this.tenant && path === this.tenant.path) return of(true);
    return this.tenantService.checkTenantPath(path);
  }

  async confirmWorkSpace() {
    if (!this.workspaceForm.valid) return;
    if (this.checkingTenantPath()) return;
    if (!this.validTenantPath()) return;
    if (this.savingWorkspace()) return;
    this.savingWorkspace.set(true);
    this.onDone.emit(this.workspaceForm.value);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
